import { useState } from 'react';
import { useSite } from '../context/SiteContext';
import Skeleton from 'react-loading-skeleton';
import { sendData } from '../services/apiCalls';
import '../styles/footer.scss';
import wayalogo from "../assets/newlo.svg";
import cbnLogo from "../assets/images/legal/cbn.png";
import ndicLogo from "../assets/images/legal/ndic.png";

function PageFooter() {
  const { settings, loading } = useSite();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState({ show: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const res = await sendData({ data: { email }, url: 'subscribe' });
    if (res.status) {
      setResponse({ status: 'success', message: res.message, show: true });
      setEmail('');
    } else {
      setResponse({
        status: 'warning',
        message: res.message || res,
        show: true,
      });
    }
    setIsSubmitting(false);
  };

  return (
    <footer className="waya-footer-wrapper">
      {response.show && (
        <div className={`alert alert-${response.status} mb-4`} role='alert'>
          {response.message}
        </div>
      )}
      
      <div className="waya-footer-grid">
        {/* Brand Column */}
        <div className="d-flex flex-column gap-3">
          {loading ? (
            <Skeleton width={100} height={28} />
          ) : (
            <img src={settings.logo_url || wayalogo} alt="WayaBank" style={{ height: '28px', filter: 'brightness(0) invert(1)', width: 'fit-content' }} onError={(e) => { e.target.src = wayalogo; }} />
          )}
          <p className="waya-footer-desc" style={{ marginBottom: '16px' }}>
            WayaBank — The bank that works for you.
          </p>
          
          <div className="waya-footer-social-container">
            <a href="https://twitter.com/WayabankNg" target="_blank" rel="noreferrer" className="waya-footer-social">
              <img src="/twitter.png" alt="Twitter" style={{ width: '15px', filter: 'brightness(0) invert(1)' }} />
            </a>
            <a href="https://www.facebook.com/wayabank?mibextid=LQQJ4d" target="_blank" rel="noreferrer" className="waya-footer-social">
              <img src="/facebook.png" alt="Facebook" style={{ width: '15px', filter: 'brightness(0) invert(1)' }} />
            </a>
            <a href="https://www.instagram.com/wayabankng" target="_blank" rel="noreferrer" className="waya-footer-social">
              <img src="/instagram.png" alt="Instagram" style={{ width: '15px', filter: 'brightness(0) invert(1)' }} />
            </a>
          </div>

          <div className="mt-4">
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', textAlign: 'inherit' }}>Subscribe to our newsletter</p>
            <form onSubmit={handleSubmit} className="waya-newsletter-form">
              <input
                className="waya-newsletter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="Email address"
                required
              />
              <button type="submit" className="waya-newsletter-btn">
                {isSubmitting ? '...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="waya-footer-heading">Company</div>
          <a href="/about" className="waya-footer-link">About WayaBank</a>
          <a href="/blogs" className="waya-footer-link">Blog</a>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="waya-footer-heading">Products</div>
          <a href="/#personal" className="waya-footer-link">Personal Banking</a>
          <a href="/#business" className="waya-footer-link">Business Banking</a>
          <a href="https://documenter.getpostman.com/view/19310490/UVeKqQWd" target="_blank" rel="noreferrer" className="waya-footer-link">API Documentation</a>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="waya-footer-heading">Support</div>
          <a href="/#faq" className="waya-footer-link">FAQs</a>
          <a href="/contact" className="waya-footer-link">Contact</a>
          <a href="mailto:customerservice@wayapaychat.com" className="waya-footer-link" style={{ textTransform: 'lowercase' }}>email address: customerservice@wayapaychat.com</a>
          <a href="tel:0700CALLWAYA" className="waya-footer-link">0700CALLWAYA</a>
          <a href="/support" className="waya-footer-link">Live Chat: wayabank.ng/support</a>
          <a href={`https://wa.me/${settings.whatsapp_number}`} className="waya-footer-link">WhatsApp: +{settings.whatsapp_number}</a>
          <div className="waya-footer-link" style={{ textTransform: 'none', lineHeight: '1.4' }}>
            📍 {settings.address || "14 Yeye olofin street, Lekki, Lagos, Nigeria"}
          </div>
          
          <div className="waya-footer-heading mt-3">Legal</div>
          <a href="/terms-of-use" className="waya-footer-link">Terms & Conditions</a>
          <a href="/privacy-page" className="waya-footer-link">Privacy Policy</a>
        </div>
      </div>

      <div className="waya-footer-divider"></div>

      <div className="waya-footer-bottom">
        <div className="d-flex flex-column gap-3 mb-3 mb-md-0 w-100">
          <div className="waya-regulatory-box" style={{ justifyContent: 'flex-start' }}>
            <span>We are licensed by</span>
            {loading ? (
              <Skeleton width={60} height={20} />
            ) : (
              <img src={settings.cbn_logo || cbnLogo} alt="CBN" style={{ height: '24px', opacity: 0.8, filter: 'brightness(1.2)' }} onError={(e) => { e.target.src = cbnLogo; }} />
            )}
            <span>and insured by</span>
            {loading ? (
              <Skeleton width={60} height={20} />
            ) : (
              <img src={settings.ndic_logo || ndicLogo} alt="NDIC" style={{ height: '18px', opacity: 0.8 }} onError={(e) => { e.target.src = ndicLogo; }} />
            )}
          </div>
          <p className="waya-footer-desc" style={{ fontSize: '11px', maxWidth: '100%', opacity: 0.5, lineHeight: '1.6' }}>
            {settings.footer_description || "WayaBank is a product of WayaLinks Limited. [CBN Licence No. XXXXX]. Customer deposits are insured by the Nigeria Deposit Insurance Corporation (NDIC). Registered address: 14 Yeye olofin street, Lekki, Lagos, Nigeria. RC No. [Registered Company Number]."}
          </p>
          <div style={{ opacity: 0.6, fontSize: '12px' }}>© {new Date().getFullYear()} {settings.site_name || 'WayaBank'}. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}

export default PageFooter;
